// PricingPage.tsx
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Check, ChevronRight, ArrowRight, Zap, Shield, Database, Building2, Users, Star } from 'lucide-react'

interface Props { dark: boolean; apiBase: string; productUrl: string }

let PRODUCT_URL: string
if (!import.meta.env.VITE_NODE_ENV || import.meta.env.VITE_NODE_ENV === 'dev') {
  PRODUCT_URL = 'http://localhost:5173/stocksignalspro#home'
} else {
  PRODUCT_URL = import.meta.env.VITE_PRODUCT_URL || 'https://stocksignalspro.larxnet.com/'
}

/* ─── Plan data ─────────────────────────────────────────────────────────────── */
const PLANS = [
  {
    name: "Starter",
    slug: "starter",
    price: { monthly: 0, annual: 0 },
    desc: "Perfect to explore the platform at zero risk.",
    features: [
      "One watchlist",
      "5 stocks in watchlist",
      "Real-time prices",
      "Unlimited alerts",
      "Advanced analytics",
    ],
    cta: "Get Started Free",
    href: "/register",
    featured: false,
    color: "rgba(255,255,255,0.06)",
    accent: "var(--c-text-3)",
  },
  {
    name: "Professional",
    slug: "pro",
    price: { monthly: 49, annual: 39 },
    desc: "For serious traders who demand edge.",
    features: [
      "Unlimited watchlists",
      "15 stocks in watchlist",
      "Advanced analytics",
      "AI-powered insights & narratives",
      "Priority support (< 4h response)",
      "Unlimited alerts",
      "Real-time data (< 50ms)",
    ],
    cta: "Get Started",
    href: "/register",
    featured: true,
    color: "rgba(34,211,238,0.06)",
    accent: "var(--c-cyan)",
  },
  {
    name: "Enterprise",
    slug: "enterprise",
    price: { monthly: null, annual: null },
    desc: "Custom infrastructure for institutions.",
    features: [
      "Everything in Professional",
      "Custom indicator development",
      "Multi-seat team accounts",
      "SLA guarantee (99.99%)",
      "White-label options",
      "Dedicated account manager",
    ],
    cta: "Talk to Sales",
    href: "/contact",
    featured: false,
    color: "rgba(245,158,11,0.05)",
    accent: "var(--c-amber)",
  },
];

const FAQ = [
  {
    q: 'Can I switch plans at any time?',
    a: 'Yes. You can upgrade or downgrade your plan at any time. Upgrades take effect immediately; downgrades apply at the end of your billing cycle.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit and debit cards (Visa, Mastercard, Amex) as well as PayPal.',
  },
  {
    q: 'Is there a free trial for Professional?',
    a: 'Yes — every new Professional sign-up gets a 7-day free trial with full access and no credit card required.',
  },
  {
    q: 'What does "unlimited signals" mean?',
    a: 'Professional and Enterprise plans have no daily limit on signal generation. Signals are generated continuously in real-time as market conditions change.',
  },
  {
    q: 'How does the annual discount work?',
    a: 'When you pay annually, you save ~20% compared to the monthly rate. Annual plans are billed as a single upfront payment.',
  },
  {
    q: 'Can Enterprise plans be customised?',
    a: 'Absolutely. Enterprise plans are fully tailored — including custom indicator development, dedicated infrastructure, SLA terms, and white-label configuration. Contact sales to discuss your requirements.',
  },
]

/* ─── FAQ item ───────────────────────────────────────────────────────────────── */
const FAQItem: React.FC<{ q: string; a: string }> = ({ q, a }) => {
  const [open, setOpen] = useState(false)
  return (
    <div
      className="rounded-2xl overflow-hidden cursor-pointer"
      style={{ border: '1px solid var(--c-border)' }}
      onClick={() => setOpen(!open)}
    >
      <div
        className="flex items-center justify-between p-5 gap-4"
        style={{ background: open ? 'rgba(34,211,238,0.04)' : 'transparent' }}
      >
        <span className="font-semibold text-sm" style={{ color: 'var(--c-text)' }}>{q}</span>
        <ChevronRight
          className="w-4 h-4 flex-shrink-0 transition-transform duration-200"
          style={{
            color: 'var(--c-text-3)',
            transform: open ? 'rotate(90deg)' : 'none',
          }}
        />
      </div>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-sm leading-relaxed" style={{ color: 'var(--c-text-2)' }}>{a}</p>
        </div>
      )}
    </div>
  )
}

/* ─── PricingPage ─────────────────────────────────────────────────────────── */
const PricingPage: React.FC<Props> = ({ dark }) => {
  const [annual, setAnnual] = useState(false)

  const borderColor = 'var(--c-border)'

  return (
    <>
      <Helmet>
        <title>Pricing — DataSignals Pro</title>
        <meta
          name="description"
          content="Simple, transparent pricing for every team size. Start free and scale as you grow."
        />
      </Helmet>

      <div className="min-h-screen page-enter" style={{ background: 'var(--c-bg)' }}>

        {/* ── HERO ──────────────────────────────────────────────────────────── */}
        <section className="relative py-24 lg:py-32 px-4 sm:px-6 overflow-hidden">
          {/* Background glow */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse at 50% -10%, rgba(34,211,238,0.10) 0%, rgba(99,102,241,0.05) 40%, transparent 70%)',
            }}
          />
          <div className="absolute inset-0 bg-dots pointer-events-none" />

          <div className="relative max-w-3xl mx-auto text-center">
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold mb-7"
              style={{
                background: 'rgba(34,211,238,0.07)',
                border: '1px solid rgba(34,211,238,0.20)',
                color: 'var(--c-cyan)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              <Star className="w-3.5 h-3.5" /> Pricing
            </div>

            <h1
              className="font-display leading-none mb-5"
              style={{
                fontSize: 'clamp(3rem, 8vw, 6.5rem)',
                color: 'var(--c-text)',
              }}
            >
              SIMPLE,{' '}
              <span
                style={{
                  background: 'linear-gradient(120deg, var(--c-cyan) 0%, var(--c-indigo) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                TRANSPARENT
              </span>
              <br />
              PRICING
            </h1>

            <p className="text-lg max-w-lg mx-auto mb-10" style={{ color: 'var(--c-text-2)' }}>
              Start free, no credit card required. Scale as your trading intelligence needs grow.
            </p>

            {/* Billing toggle */}
            <div
              className="inline-flex items-center gap-3 p-1 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--c-border)' }}
            >
              <button
                onClick={() => setAnnual(false)}
                className="px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                style={{
                  background: !annual ? 'rgba(34,211,238,0.12)' : 'transparent',
                  color: !annual ? 'var(--c-cyan)' : 'var(--c-text-2)',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setAnnual(true)}
                className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                style={{
                  background: annual ? 'rgba(34,211,238,0.12)' : 'transparent',
                  color: annual ? 'var(--c-cyan)' : 'var(--c-text-2)',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Annual
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-bold"
                  style={{
                    background: 'rgba(16,185,129,0.15)',
                    color: 'var(--c-green)',
                  }}
                >
                  Save 20%
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* ── PRICING CARDS ─────────────────────────────────────────────────── */}
        <section className="pb-24 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
              {PLANS.map((plan, idx) => (
                <div
                  key={plan.slug}
                  className="relative"
                  style={{ marginTop: plan.featured ? 0 : 0 }}
                >
                  {/* Most Popular badge */}
                  {plan.featured && (
                    <div
                      className="absolute -top-4 left-1/2 -translate-x-1/2 z-10 px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap"
                      style={{
                        background: 'linear-gradient(135deg, #0891b2, #6366f1)',
                        color: '#fff',
                        boxShadow: '0 2px 16px rgba(34,211,238,0.30)',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      ★ Most Popular
                    </div>
                  )}

                  <div
                    className="glass rounded-2xl overflow-hidden h-full flex flex-col transition-all duration-300 hover:-translate-y-1"
                    style={{
                      boxShadow: plan.featured
                        ? '0 0 0 1px rgba(34,211,238,0.25), 0 32px 64px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.08) inset'
                        : '0 8px 32px rgba(0,0,0,0.3)',
                    }}
                  >
                    {/* Top accent line */}
                    <div
                      style={{
                        height: '2px',
                        background: plan.featured
                          ? 'linear-gradient(90deg, transparent, var(--c-cyan), var(--c-indigo), transparent)'
                          : `linear-gradient(90deg, transparent, ${plan.accent}, transparent)`,
                        opacity: plan.featured ? 1 : 0.5,
                      }}
                    />

                    <div className="p-7 flex flex-col flex-1">
                      {/* Plan header */}
                      <div className="mb-7">
                        <div
                          className="text-xs font-bold tracking-widest uppercase mb-3"
                          style={{ fontFamily: 'var(--font-mono)', color: plan.accent }}
                        >
                          {plan.name}
                        </div>

                        {/* Price */}
                        <div className="flex items-baseline gap-1.5 mb-2">
                          {plan.price.monthly === null ? (
                            <span
                              className="font-display"
                              style={{
                                fontSize: 'clamp(2rem,4vw,2.75rem)',
                                color: 'var(--c-text)',
                              }}
                            >
                              Custom
                            </span>
                          ) : plan.price.monthly === 0 ? (
                            <span
                              className="font-display"
                              style={{
                                fontSize: 'clamp(2rem,4vw,2.75rem)',
                                background: 'linear-gradient(120deg, var(--c-cyan) 0%, var(--c-indigo) 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                              }}
                            >
                              Free
                            </span>
                          ) : (
                            <>
                              <span
                                className="font-display"
                                style={{
                                  fontSize: 'clamp(2rem,4vw,2.75rem)',
                                  background: 'linear-gradient(120deg, var(--c-cyan) 0%, var(--c-indigo) 100%)',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                  backgroundClip: 'text',
                                }}
                              >
                                ${annual ? plan.price.annual : plan.price.monthly}
                              </span>
                              <span className="text-sm" style={{ color: 'var(--c-text-3)' }}>/mo</span>
                              {annual && (
                                <span
                                  className="text-xs ml-1 px-2 py-0.5 rounded-full"
                                  style={{
                                    background: 'rgba(16,185,129,0.12)',
                                    color: 'var(--c-green)',
                                  }}
                                >
                                  billed annually
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        <p className="text-xs leading-relaxed" style={{ color: 'var(--c-text-2)' }}>
                          {plan.desc}
                        </p>
                      </div>

                      {/* Features */}
                      <ul className="space-y-2.5 mb-8 flex-1">
                        {plan.features.map((feat) => (
                          <li key={feat} className="flex items-start gap-2.5 text-sm" style={{ color: 'var(--c-text-2)' }}>
                            <Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: 'var(--c-green)' }} />
                            {feat}
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <Link
                        to={plan.href}
                        className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] ${
                          plan.featured ? 'btn-cta' : 'btn-ghost'
                        }`}
                      >
                        {plan.cta} <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>

                  {/* Drop light below each card */}
                  <div
                    className="pointer-events-none"
                    style={{
                      position: 'absolute',
                      bottom: '-18px',
                      left: '15%',
                      right: '15%',
                      height: '30px',
                      background: `radial-gradient(ellipse at 50% 0%, ${
                        plan.featured
                          ? 'rgba(34,211,238,0.20)'
                          : plan.slug === 'enterprise'
                          ? 'rgba(245,158,11,0.12)'
                          : 'rgba(255,255,255,0.06)'
                      } 0%, transparent 70%)`,
                      filter: 'blur(6px)',
                    }}
                  />
                </div>
              ))}
            </div>

            {/* All-plans note */}
            <p className="text-center mt-12 text-sm" style={{ color: 'var(--c-text-3)' }}>
              All plans include SSL encryption, uptime monitoring, and GDPR compliance.{' '}
              <Link to="/contact" style={{ color: 'var(--c-cyan)' }} className="hover:underline">
                Need a custom plan? Talk to us →
              </Link>
            </p>
          </div>
        </section>

        {/* ── WHO IT'S FOR ──────────────────────────────────────────────────── */}
        <section className="py-20 px-4 sm:px-6" style={{ borderTop: `1px solid ${borderColor}` }}>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <h2
                className="font-display leading-none"
                style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', color: 'var(--c-text)' }}
              >
                WHO IT'S{' '}
                <span
                  style={{
                    background: 'linear-gradient(120deg, var(--c-cyan) 0%, var(--c-indigo) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  BUILT FOR
                </span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              {[
                {
                  icon: <Zap className="w-6 h-6" />,
                  title: 'Active Traders',
                  desc: 'Speed and precision matter when every second counts. Real-time signals and AI narratives keep you ahead of the curve.',
                  color: '#22d3ee',
                  plan: 'Professional',
                },
                {
                  icon: <Users className="w-6 h-6" />,
                  title: 'Quant Analysts',
                  desc: 'Build, backtest, and iterate on signal strategies via our full API. 20+ indicators and machine-readable outputs ready.',
                  color: '#818cf8',
                  plan: 'Professional',
                },
                {
                  icon: <Building2 className="w-6 h-6" />,
                  title: 'Institutions',
                  desc: 'Dedicated infrastructure, white-label options, and enterprise SLAs built for compliance-driven environments.',
                  color: '#f59e0b',
                  plan: 'Enterprise',
                },
              ].map((card) => (
                <div key={card.title} className="glass rounded-2xl p-6">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
                    style={{
                      background: `${card.color}18`,
                      border: `1px solid ${card.color}30`,
                      color: card.color,
                    }}
                  >
                    {card.icon}
                  </div>
                  <h3 className="font-bold text-base mb-2" style={{ color: 'var(--c-text)' }}>{card.title}</h3>
                  <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--c-text-2)' }}>{card.desc}</p>
                  <span
                    className="text-xs font-semibold px-2.5 py-1 rounded-full"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid var(--c-border)',
                      color: 'var(--c-text-3)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {card.plan} →
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────────────────── */}
        <section className="py-20 px-4 sm:px-6" style={{ borderTop: `1px solid ${borderColor}` }}>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2
                className="font-display leading-none"
                style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', color: 'var(--c-text)' }}
              >
                FREQUENTLY ASKED
              </h2>
            </div>
            <div className="space-y-3">
              {FAQ.map((item) => (
                <FAQItem key={item.q} q={item.q} a={item.a} />
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ───────────────────────────────────────────────────────────── */}
        <section
          className="relative py-24 px-4 sm:px-6 overflow-hidden"
          style={{ borderTop: `1px solid ${borderColor}` }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse at 50% 50%, rgba(8,145,178,0.10) 0%, rgba(99,102,241,0.05) 50%, transparent 70%)',
            }}
          />
          <div className="relative max-w-xl mx-auto text-center">
            <h2
              className="font-display leading-none mb-5"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', color: 'var(--c-text)' }}
            >
              START FREE<br />
              <span
                style={{
                  background: 'linear-gradient(120deg, var(--c-cyan) 0%, var(--c-indigo) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                TODAY
              </span>
            </h2>
            <p className="text-base mb-8" style={{ color: 'var(--c-text-2)' }}>
              No credit card required. Upgrade any time.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="btn-cta group inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <a
                href={PRODUCT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold"
              >
                View Live Platform
              </a>
            </div>
            <div className="flex flex-wrap justify-center gap-6 mt-7 text-sm" style={{ color: 'var(--c-text-3)' }}>
              {['No credit card required', '7-day free trial on Pro', 'Cancel any time'].map((txt) => (
                <span key={txt} className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5" style={{ color: 'var(--c-cyan)' }} />
                  {txt}
                </span>
              ))}
            </div>
          </div>
        </section>

      </div>
    </>
  )
}

export default PricingPage
